import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ClipboardInput from './ClipboardInput';
import ClipboardItem from './ClipboardItem';
import ConnectionStatus from './ConnectionStatus';
import ExpirationBanner from './ExpirationBanner';
import RoomShareModal from './RoomShareModal';
import ExitConfirmationModal from './ExitConfirmationModal';
import { socketService } from '../services/socketService';
import { roomService } from '../services/roomService';
import toast from 'react-hot-toast';

const { FiLogOut, FiShare2 } = FiIcons;

export default function ClipboardRoom({ roomName, password, onLeave }) {
  const [items, setItems] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [userCount, setUserCount] = useState(1);
  const [connectedDevices, setConnectedDevices] = useState([]);
  const [timeRemaining, setTimeRemaining] = useState(60);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);

  useEffect(() => {
    const socket = socketService.connect();
    
    const checkExpiration = async () => {
      const result = await roomService.getRoomExpiration(roomName);
      if (result.success) {
        setTimeRemaining(result.timeRemaining);
        if (result.isExpired) {
          toast.error('Room has expired');
          onLeave();
        }
      }
    };

    checkExpiration();
    const timerInterval = setInterval(checkExpiration, 60000);

    const handleConnect = () => {
      setIsConnected(true);
      socket.emit('join_room', { room: roomName, password });
    };

    const handleSync = (syncItems) => setItems(syncItems);
    const handleNewItem = (item) => setItems(prev => prev.some(i => i.id === item.id) ? prev : [item, ...prev]);
    const handleItemUpdate = (updatedItem) => setItems(prev => prev.map(i => i.id === updatedItem.id ? updatedItem : i));
    const handleUserCount = (data) => setUserCount(data.count);
    const handleDevices = (devices) => setConnectedDevices(devices);
    const handleTerminated = (data) => {
      toast.error(data.reason || 'Room closed');
      onLeave();
    };

    socket.on('connect', handleConnect);
    socket.on('clipboard_sync', handleSync);
    socket.on('new_clipboard_item', handleNewItem);
    socket.on('item_updated', handleItemUpdate);
    socket.on('user_count_update', handleUserCount);
    socket.on('connected_devices', handleDevices);
    socket.on('room_terminated', handleTerminated);

    return () => {
      clearInterval(timerInterval);
      socket.off('connect', handleConnect);
      socket.off('clipboard_sync', handleSync);
      socket.off('new_clipboard_item', handleNewItem);
      socket.off('item_updated', handleItemUpdate);
      socket.off('user_count_update', handleUserCount);
      socket.off('connected_devices', handleDevices);
      socket.off('room_terminated', handleTerminated);
      socketService.disconnect();
    };
  }, [roomName, password, onLeave]);

  const handleAdd = async (payload) => {
    const socket = socketService.getSocket();
    if (socket) {
      socket.emit('add_clipboard_item', { room: roomName, item: { ...payload, deviceName: socketService.getDeviceId() } });
      return true;
    }
    return false;
  };

  const handleConfirmDestruction = async () => {
    try {
      const socket = socketService.getSocket();
      if (socket) socket.emit('destroy_room', { room: roomName });
      await roomService.expireRoomNow(roomName);
      toast.success('Room destroyed');
      onLeave();
    } catch (error) {
      onLeave();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8 pb-24">
      <div className="max-w-4xl mx-auto">
        <ExpirationBanner timeRemaining={timeRemaining} />
        
        <header className="bg-white rounded-[2.5rem] p-6 md:p-8 shadow-sm border border-gray-100 flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl md:text-3xl font-black text-gray-900 tracking-tight">Room: {roomName}</h1>
              <motion.button 
                whileHover={{ scale: 1.1 }} 
                whileTap={{ scale: 0.9 }} 
                onClick={() => setShowShareModal(true)}
                className="p-2.5 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-100 transition-all shadow-sm flex items-center gap-2"
              >
                <SafeIcon icon={FiShare2} className="w-5 h-5" />
                <span className="text-xs font-black uppercase tracking-widest hidden sm:inline">Invite</span>
              </motion.button>
            </div>
            <ConnectionStatus isConnected={isConnected} userCount={userCount} deviceId={socketService.getDeviceId()} connectedDevices={connectedDevices} />
          </div>
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setShowExitModal(true)}
              className="px-8 py-4 bg-red-50 text-red-600 rounded-2xl hover:bg-red-100 transition-all flex items-center justify-center gap-2 font-black text-sm uppercase tracking-widest"
            >
              <SafeIcon icon={FiLogOut} className="w-5 h-5" />
              Leave Room
            </button>
          </div>
        </header>

        <ClipboardInput onAddClipboardItem={handleAdd} isConnected={isConnected} roomName={roomName} />

        <div className="mt-12">
          <div className="flex items-center justify-between mb-8 px-2">
            <h2 className="text-xl font-black text-gray-900 flex items-center gap-3 uppercase tracking-tighter">
              Shared Clipboard
              <span className="px-3 py-1 bg-gray-900 text-white rounded-full text-[10px] font-black">
                {items.length} CLIPS
              </span>
            </h2>
          </div>

          <div className="space-y-6">
            <AnimatePresence mode="popLayout">
              {items.length === 0 ? (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-center py-32 bg-white rounded-[3rem] border-2 border-dashed border-gray-100">
                  <div className="w-24 h-24 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
                    <SafeIcon icon={FiShare2} className="w-10 h-10 text-gray-200" />
                  </div>
                  <p className="text-gray-400 font-black text-xl uppercase tracking-tighter">The floor is yours</p>
                  <p className="text-gray-400 text-sm mt-1 font-medium">Nothing shared yet. Start by pasting above.</p>
                </motion.div>
              ) : (
                items.map((item, idx) => (
                  <ClipboardItem key={item.id || idx} item={item} index={idx} />
                ))
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      <RoomShareModal isOpen={showShareModal} onClose={() => setShowShareModal(false)} roomName={roomName} password={password} />
      <ExitConfirmationModal isOpen={showExitModal} onClose={() => setShowExitModal(false)} onConfirm={handleConfirmDestruction} itemCount={items.length} />
    </div>
  );
}